import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import Sidebar from "~/components/Sidebar/Sidebar.tsx";
import { QUOTAS_INDEX_PATH } from "~/constants/appRoutes.ts";
import { processedInvoiceQueries } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import {
	DeleteQuotaForm,
} from "~/modules/quota/components/QuotaDetailsView/components/DeleteQuotaSidebar/DeleteQuotaForm.tsx";
import {
	getQuotaConsumerProjectIds,
	invalidateProjectQuotaQueries,
	invalidateQuotaQueries,
} from "~/modules/quota/utils/quotaCacheUtils.ts";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	quota: QuotaWithRelations;
};

export const DeleteQuotaSidebar = ({ isOpen, onClose, quota }: Props) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const handleSuccess = useCallback(() => {
		// The quota does not exist anymore. A refetch of its details would fail.
		queryClient.removeQueries({ queryKey: [...quotaQueries.baseKey, "details", quota.id] });
		void invalidateQuotaQueries(queryClient);
		// The delete frees the consumer orders.
		invalidateProjectQuotaQueries(queryClient, getQuotaConsumerProjectIds(quota.consumers));
		// The delete also deletes the advance invoice. The validation refuses a sent or paid advance invoice.
		// The invoices of an invoice-backed quota stay, and only lose their quota link. The invalidation
		// below reloads them.
		if (quota.type === "advance") {
			quota.invoices.forEach((invoice) => {
				queryClient.removeQueries({ queryKey: processedInvoiceQueries.details(invoice.id).queryKey });
			});
		}
		void queryClient.invalidateQueries({ queryKey: processedInvoiceQueries.baseKey });
		onClose();
		navigate(QUOTAS_INDEX_PATH);
	}, [navigate, onClose, queryClient, quota.consumers, quota.id, quota.invoices, quota.type]);

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={onClose}>
			{isOpen && <DeleteQuotaForm onCancel={onClose}
										onSuccess={handleSuccess}
										quota={quota} />}
		</Sidebar>
	);
};
